import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { saasApi } from '../../lib/api';

function fmtMoney(cents, currency = 'BRL') {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString('pt-BR') : '—';
}

const statusLabel = { PENDING: 'Pendente', PAID: 'Paga', FAILED: 'Falhou', CANCELED: 'Cancelada' };
const methodLabel = { STRIPE: 'Stripe', PIX: 'Pix', MANUAL: 'Manual' };

export default function AdminPayments() {
  const [invoices, setInvoices] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  function load() {
    setError('');
    return saasApi.listInvoices().then(setInvoices).catch((e) => setError(e.message));
  }

  useEffect(() => {
    load();
  }, []);

  async function pay(id) {
    setBusy(true);
    setError('');
    try {
      await saasApi.payInvoice(id);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  const totalPending = invoices.filter((i) => i.status === 'PENDING').reduce((acc, i) => acc + i.amountCents, 0);
  const totalPaid = invoices.filter((i) => i.status === 'PAID').reduce((acc, i) => acc + i.amountCents, 0);

  return (
    <div className="flex flex-col gap-base">
      <div>
        <h1 className="studio-uniform-title font-h2 text-h2 text-on-surface">Pagamentos</h1>
        <p className="font-body-md text-body-md text-on-surface-variant mt-2">Faturas de todos os tenants.</p>
      </div>

      {error && <p className="text-[11px] text-error">{error}</p>}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <div className="bg-surface rounded-2xl shadow-sm p-5">
          <p className="font-label-sm text-label-sm text-on-surface-variant">Faturas</p>
          <p className="mt-1 font-h3 text-h3 text-on-surface">{invoices.length}</p>
        </div>
        <div className="bg-surface rounded-2xl shadow-sm p-5">
          <p className="font-label-sm text-label-sm text-on-surface-variant">A receber</p>
          <p className="mt-1 font-h3 text-h3 text-on-surface">{fmtMoney(totalPending)}</p>
        </div>
        <div className="bg-surface rounded-2xl shadow-sm p-5">
          <p className="font-label-sm text-label-sm text-on-surface-variant">Recebido</p>
          <p className="mt-1 font-h3 text-h3 text-on-surface">{fmtMoney(totalPaid)}</p>
        </div>
      </div>

      <div className="bg-surface rounded-2xl shadow-sm overflow-hidden">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="border-b border-surface-variant text-left font-label-sm text-label-sm text-on-surface-variant">
              <th className="px-5 py-3">Tenant</th>
              <th className="px-5 py-3">Plano</th>
              <th className="px-5 py-3">Valor</th>
              <th className="px-5 py-3">Método</th>
              <th className="px-5 py-3">Vencimento</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.id} className="border-b border-surface-container-highest hover:bg-surface-container-low transition-colors">
                <td className="px-5 py-3">
                  <Link to={`/admin/tenants/${inv.tenant.id}`} className="font-medium text-on-surface hover:text-primary">
                    {inv.tenant.name}
                  </Link>
                  <div className="text-[10px] text-on-surface-variant">{inv.tenant.slug}</div>
                </td>
                <td className="px-5 py-3 text-on-surface-variant">{inv.subscription?.plan?.name ?? inv.tenant.plan?.name ?? '—'}</td>
                <td className="px-5 py-3 text-on-surface">{fmtMoney(inv.amountCents, inv.currency)}</td>
                <td className="px-5 py-3 text-on-surface-variant">{methodLabel[inv.paymentMethod] ?? inv.paymentMethod}</td>
                <td className="px-5 py-3 text-on-surface-variant">{fmtDate(inv.dueAt)}</td>
                <td className="px-5 py-3">
                  <Badge tone={inv.status}>{statusLabel[inv.status]}</Badge>
                </td>
                <td className="px-5 py-3 text-right">
                  {inv.status === 'PENDING' && (
                    <button
                      onClick={() => pay(inv.id)}
                      disabled={busy}
                      className="rounded-lg bg-tertiary-container px-3 py-1.5 text-[10px] font-medium text-on-tertiary-container disabled:opacity-50"
                    >
                      Confirmar Pix
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {invoices.length === 0 && (
              <tr>
                <td colSpan={7} className="px-5 py-6 text-center text-on-surface-variant">
                  Nenhuma fatura.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Badge({ tone, children }) {
  const styles = {
    PAID: 'bg-tertiary-container text-on-tertiary-container',
    PENDING: 'bg-surface-container-highest text-on-surface-variant',
    FAILED: 'bg-error-container text-on-error-container',
    CANCELED: 'bg-surface-container-highest text-on-surface-variant',
  };
  return <span className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-label-sm ${styles[tone] ?? ''}`}>{children}</span>;
}


